import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { CopyableId } from '../../../components/CopyableId';
import { TenantName } from '../../../components/TenantName';
import { formatDateTime, formatInteger } from '../../../lib/format';
import { formatCredits } from '../../../lib/priceLadder';
import { SendProgress } from './SendProgress';
import { broadcastTitle, channelOf, CHANNEL_LABEL_KEYS, doneOf, stateOf, STATE_TONE, STATE_LABEL_KEYS } from '../lifecycle';
import { useT } from '../../../i18n/I18nProvider';

type Broadcast = admin.AdminBroadcast;

/**
 * One broadcast, with the identifiers that let an operator close the loop
 * between "this tenant is blasting" and "this number's quality rating just
 * dropped". The number id is the join to `/gateways/numbers`, which is the
 * incident this page exists to catch, so it is here with a copy action rather
 * than printed in the table where it would be noise.
 */
export function BroadcastDrawer({ row, onClose }: { row: Broadcast | null; onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();

  return (
    <Drawer opened={row !== null} onClose={onClose} title={t('broadcasts.drawer.title')} size="md">
      {row && (
        <Stack gap="lg">
          <Stack gap="sm">
            <Text role="subtitle" fw={600} style={{ overflowWrap: 'anywhere' }}>{broadcastTitle(row, t)}</Text>
            <Group gap="sm">
              <Badge size="sm" variant="light" color="gray">{t(CHANNEL_LABEL_KEYS[channelOf(row)])}</Badge>
              <StatusBadge status={STATE_TONE[stateOf(row)]}>
                {t(STATE_LABEL_KEYS[stateOf(row)])}
              </StatusBadge>
              {row.recipients_failed > 0 && (
                <Badge size="sm" color="red" variant="light">
                  {t('broadcasts.failedCount', { count: formatInteger(row.recipients_failed) })}
                </Badge>
              )}
            </Group>
          </Stack>

          <SendProgress sent={row.recipients_sent} done={doneOf(row)} total={row.recipients_total} />

          {row.failure_code && (
            <Alert color="error" variant="light" title={t('broadcasts.drawer.failureCode')}>
              <code>{row.failure_code}</code>
            </Alert>
          )}

          <Stack gap="md">
            <Field label={t('broadcasts.drawer.tenant')}>
              <TenantName id={row.organisation_id} withCopy />
            </Field>
            <Field label={t('broadcasts.drawer.recipients')}>
              <Group gap="md" wrap="wrap">
                <Counter label={t('status.sent')} value={row.recipients_sent} />
                <Counter label={t('common.total')} value={row.recipients_total} />
                <Counter label={t('broadcasts.columns.delivered')} value={row.recipients_delivered} />
                {channelOf(row) === 'whatsapp' && <Counter label={t('broadcasts.columns.read')} value={row.recipients_read} />}
                <Counter label={t('status.failed')} value={row.recipients_failed} />
                <Counter label={t('broadcasts.columns.filtered')} value={row.recipients_filtered} />
              </Group>
            </Field>
            {row.estimated_cost_minor != null && (
              <Field label={t('broadcasts.drawer.estimate')}>
                <Text role="bodySmall">{t('broadcasts.drawer.estimateValue', { amount: formatCredits(row.estimated_cost_minor) })}</Text>
              </Field>
            )}
            <Field label={t('broadcasts.drawer.throttle')}>
              <Text role="bodySmall">{t('broadcasts.drawer.throttleValue', { tps: formatInteger(row.throttle_per_second) })}</Text>
            </Field>
            <Field label={t('broadcasts.drawer.timeline')}>
              <Stack gap="xs">
                <Moment label={t('common.created')} at={row.created_at} />
                <Moment label={t('broadcasts.drawer.scheduled')} at={row.scheduled_at} />
                <Moment label={t('broadcasts.drawer.started')} at={row.started_at} />
                <Moment label={t('broadcasts.drawer.finished')} at={row.finished_at} />
              </Stack>
            </Field>
            <Field label={t('broadcasts.drawer.broadcastId')}>
              <CopyableId value={row.id} label={t('broadcasts.copy.broadcastId')} />
            </Field>
            <Field label={t('broadcasts.drawer.phoneNumberId')}>
              <CopyableId value={row.phone_number_id} label={t('broadcasts.copy.phoneNumberId')} />
            </Field>
            <Field label={t('broadcasts.drawer.segmentId')}>
              {row.segment_id
                ? <CopyableId value={row.segment_id} label={t('broadcasts.copy.segmentId')} />
                : <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('broadcasts.drawer.noSegment')}</Text>}
            </Field>
          </Stack>

          <Alert color="info" variant="light">{t('broadcasts.drawer.hint')}</Alert>

          <Group gap="sm">
            <Button size="xs" onClick={() => nav(`/tenants/${row.organisation_id}`)}>
              {t('broadcasts.drawer.openTenant')}
            </Button>
            <Button size="xs" variant="outline" onClick={() => nav('/gateways/numbers')}>
              {t('broadcasts.drawer.openNumbers')}
            </Button>
            <Button size="xs" variant="subtle" onClick={onClose}>{t('action.close')}</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap="xs">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      {children}
    </Stack>
  );
}

function Counter({ label, value }: { label: string; value: number | undefined }) {
  const t = useT();
  return (
    <Stack gap="xs">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Text role="bodySmall" fw={600}>{value === undefined ? t('common.notAvailable') : formatInteger(value)}</Text>
    </Stack>
  );
}

function Moment({ label, at }: { label: string; at: string | null }) {
  const t = useT();
  return (
    <Group gap="sm" justify="space-between" wrap="nowrap">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Text role="bodySmall">{at ? formatDateTime(at) : t('common.notAvailable')}</Text>
    </Group>
  );
}
